import { z } from 'zod';

import { logDroppedBizyNode } from './bizy-node-telemetry';
import {
	CommentDraftCardSchema,
	RecognitionAppreciationCardSchema,
	RecognitionDraftCardSchema,
	RecognitionViewCardSchema,
	UserAdminBatchConfirmCardSchema,
} from './bizy-ui-blocks';
import { isoDatetimeToDate } from './dates';
import { fixture } from './fixtures';

// ─── AssistantMessage Document ───────────────────────────────────────────────

// Each node is one slice of the assistant's response. All nodes share the
// `{type, payload}` envelope — markdown payloads carry `{content}`; card
// payloads carry their card-specific fields with camelCase keys preserved
// at the wire boundary.
//
// Persisted on Bizy::ChatHistory.assistant_message and streamed live via
// BizyChannel `node_token` / `node_appended` events.

export const BizyMarkdownNode = z.object({
	type: z.literal('markdown'),
	payload: z.object({
		content: z.string(),
		role: z.string().optional(),
	}),
});

export const BizyApprovalCardNode = z.object({
	type: z.literal('approval_card'),
	payload: z.record(z.string(), z.unknown()),
});

export const BizyContentCardNode = z.object({
	type: z.literal('content_card'),
	payload: z.record(z.string(), z.unknown()),
});

export const BizyDraftCardNode = z.object({
	type: z.literal('draft_card'),
	payload: z.record(z.string(), z.unknown()),
});

// Generic card rendering a vertical stack of per-person rows with per-item CTA,
// optional subtitle, and optional completion status.
const BizyMeetingPeopleCardPerson = z
	.object({
		userId: z.string().nullable().optional(),
		participantKey: z.string().nullable().optional(),
		subtitle: z.string().nullable().optional(),
		cta: z
			.object({
				label: z.string().nullable().optional(),
				href: z.string().nullable().optional(),
			})
			.partial()
			.nullable()
			.optional(),
		status: z.string().nullable().optional(),
	})
	.passthrough();

export const BizyMeetingPeopleCardNode = z.object({
	type: z.literal('meeting_people_card'),
	payload: z
		.object({
			title: z.string().nullable().optional(),
			people: z.array(BizyMeetingPeopleCardPerson).nullable().optional(),
			// Surface attribution for the per-row click event (HABITS-1450).
			// Set to "proactive_nudge" by DeliverNotificationTask; absent on
			// the suggest_people_to_meet tool path, where the FE defaults to
			// "chat_tool". `category` mirrors the proactive moment's category
			// (e.g. "conversations") so Amplitude funnels can join the
			// CardClicked event back to TRIGGERED/OPENED on the same key.
			//
			// Both kept as plain `z.string()` (not z.enum) for forward
			// compat: a new BE-introduced surface (e.g. another category
			// embedding the card later) or a typo must NOT fail the node.
			// BizyAssistantMessageNodeListSchema below silently drops
			// nodes whose safeParse fails, so a strict enum here would
			// vanish the entire meeting_people_card from the UI on any
			// unknown value. The FE click handler still narrows to its
			// known union via `MeetingPeopleCardSurface`; an unknown wire
			// value flows through to Amplitude as-is, which is the
			// desired analytics behavior.
			cardSurface: z.string().nullable().optional(),
			category: z.string().nullable().optional(),
		})
		.passthrough(),
});

// Suggested next questions, phrased as paste-ready imperative commands.
// Rendered as one-tap rows; clicking sends the suggestion verbatim.
export const BizyFollowUpsNode = z.object({
	type: z.literal('follow_ups'),
	payload: z.object({
		suggestions: z.array(z.string()),
	}),
});

// 1–4 headline KPI cards that summarize an answer at a glance (the "verdict"
// that leads a rich Data Agent response). Nullable metric fields arrive ABSENT
// on the wire (the backend deep-compacts nils), so they are `.optional()`, not
// `.nullable()`.
const BizyScorecardMetric = z.object({
	label: z.string(),
	value: z.string(),
	percentile: z.number().optional(),
	peerMedian: z.string().optional(),
	sentiment: z.enum(['positive', 'neutral', 'negative']),
	trend: z.array(z.number()).optional(),
});

export const BizyScorecardNode = z.object({
	type: z.literal('scorecard'),
	payload: z.object({
		title: z.string().optional(),
		metrics: z.array(BizyScorecardMetric),
	}),
});

// The evidence chart that follows the verdict: `bar` for rankings/breakdowns,
// `line` for a metric over time. Nullable fields arrive ABSENT on the wire (the
// backend deep-compacts nils), so they are `.optional()`, not `.nullable()`.
const BizyChartPoint = z.object({
	label: z.string(),
	value: z.number().optional(),
	partial: z.boolean().optional(),
});

const BizyChartSeries = z.object({
	name: z.string(),
	points: z.array(BizyChartPoint),
});

export const BizyChartNode = z.object({
	type: z.literal('chart'),
	payload: z.object({
		variant: z.enum(['bar', 'line', 'stacked', 'diverging']),
		title: z.string().optional(),
		xLabel: z.string().optional(),
		yLabel: z.string().optional(),
		yFormat: z.enum(['number', 'percent', 'currency']),
		series: z.array(BizyChartSeries),
		referenceValue: z.number().optional(),
		referenceLabel: z.string().optional(),
		// Full group count when a bar/breakdown was trimmed to a subset; drives
		// the "N of M" caption + expand action. `selection` adds the ranking
		// direction (top/bottom) when one applies. Both absent when not trimmed.
		totalGroups: z.number().optional(),
		// Cosmetic caption hint — `.catch` so an unexpected value degrades to the
		// neutral "N of M" rather than failing the union and dropping the chart.
		selection: z.enum(['top', 'bottom']).optional().catch(undefined),
	}),
});

// A 2-D categorical grid (heatmap): a value at each (row, column). `cells` is
// sparse — a missing combination has no entry. Nullable wire fields arrive
// ABSENT (the backend deep-compacts nils), so they're `.optional()`.
const BizyMatrixCell = z.object({
	row: z.string(),
	column: z.string(),
	value: z.number().optional(),
});

export const BizyMatrixNode = z.object({
	type: z.literal('matrix'),
	payload: z.object({
		title: z.string().optional(),
		rowLabel: z.string().optional(),
		columnLabel: z.string().optional(),
		valueFormat: z.enum(['number', 'percent', 'currency']),
		zeroFill: z.boolean().optional(),
		rows: z.array(z.string()),
		columns: z.array(z.string()),
		cells: z.array(BizyMatrixCell),
		// Row axis is the entity ranking and can be trimmed; mirrors the table.
		totalRows: z.number().optional(),
		selection: z.enum(['top', 'bottom']).optional().catch(undefined),
	}),
});

// Structured table evidence: typed columns + positional rows (cells aligned to
// columns). The renderer owns alignment/formatting/sort/CSV/truncation. `cells`
// keeps nulls (positional), unlike the nil-stripped optional fields.
const BizyTableColumn = z.object({
	label: z.string(),
	align: z.enum(['left', 'right']),
	format: z.enum(['text', 'number', 'percent', 'currency']),
});

const BizyTableRow = z.object({
	cells: z.array(z.union([z.string(), z.number(), z.null()])),
});

export const BizyTableNode = z.object({
	type: z.literal('table'),
	payload: z.object({
		title: z.string().optional(),
		columns: z.array(BizyTableColumn),
		rows: z.array(BizyTableRow),
		totalRows: z.number().optional(),
		defaultSortColumn: z.number().optional(),
		// Ranking direction for the "N of M" caption (top/bottom); absent = neutral.
		// `.catch` so a surprise value degrades to neutral rather than dropping the table.
		selection: z.enum(['top', 'bottom']).optional().catch(undefined),
	}),
});

// The "How I got this" provenance panel that trails a data answer: what was
// queried (sourceDetails) and the limitations/assumptions (caveats). Rendered
// as a collapsible disclosure. Both fields are always present on the wire.
export const BizySourceDetailsNode = z.object({
	type: z.literal('source_details'),
	payload: z.object({
		sourceDetails: z.string(),
		caveats: z.string(),
	}),
});

export const BizyAssistantMessageNode = z.discriminatedUnion('type', [
	BizyMarkdownNode,
	BizyApprovalCardNode,
	BizyContentCardNode,
	BizyDraftCardNode,
	BizyMeetingPeopleCardNode,
	BizyFollowUpsNode,
	BizyScorecardNode,
	BizyChartNode,
	BizyMatrixNode,
	BizyTableNode,
	BizySourceDetailsNode,
	RecognitionDraftCardSchema,
	CommentDraftCardSchema,
	RecognitionViewCardSchema,
	RecognitionAppreciationCardSchema,
	UserAdminBatchConfirmCardSchema,
]);

export type BizyAssistantMessageNode = z.infer<typeof BizyAssistantMessageNode>;
export type BizyFollowUpsNode = z.infer<typeof BizyFollowUpsNode>;
export type BizyScorecardNode = z.infer<typeof BizyScorecardNode>;
export type BizyScorecardMetric = z.infer<typeof BizyScorecardMetric>;
export type BizyChartNode = z.infer<typeof BizyChartNode>;
export type BizyMatrixNode = z.infer<typeof BizyMatrixNode>;
export type BizyTableNode = z.infer<typeof BizyTableNode>;
export type BizySourceDetailsNode = z.infer<typeof BizySourceDetailsNode>;
export type BizyMarkdownNode = z.infer<typeof BizyMarkdownNode>;
export type BizyApprovalCardNode = z.infer<typeof BizyApprovalCardNode>;
export type BizyContentCardNode = z.infer<typeof BizyContentCardNode>;
export type BizyDraftCardNode = z.infer<typeof BizyDraftCardNode>;
export type BizyMeetingPeopleCardNode = z.infer<typeof BizyMeetingPeopleCardNode>;

/**
 * Tolerant array parser for persisted AssistantMessage documents.
 *
 * History rehydration ran the whole `assistant_message` array through a
 * strict `z.array(BizyAssistantMessageNode)`. A single unrecognized node
 * `type` (legacy data, forward-compat from a newer backend, malformed
 * payload) failed the array, then `BizyUserMessage.safeParse` failed,
 * then `BizyUserConversationDetail.safeParse` failed, and the conversation
 * silently disappeared from history (`if (!parsed.success) return;` in
 * `refetchConversation` / `loadConversation`).
 *
 * This transform drops invalid nodes individually instead — a corrupt or
 * unknown card no longer takes the rest of the conversation down with it.
 * Each drop is logged (schema drift) so a silently vanished node is still
 * observable rather than invisible.
 */
export const BizyAssistantMessageNodeListSchema = z.array(z.unknown()).transform((raw) =>
	raw.flatMap((item) => {
		const result = BizyAssistantMessageNode.safeParse(item);
		if (result.success) return [result.data];
		logDroppedBizyNode('rehydrate', item, result.error);
		return [];
	})
);

// ─── Index (list) item ───────────────────────────────────────────────────────

export const BizyUserConversation = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	messageCount: z.number().int(),
	// Nullable because proactive (assistant-first) conversations have no user
	// message yet. The backend falls back to bizy_response in that case, but
	// keep this defensive — an empty conversation row should never break the
	// dropdown / history list.
	firstUserMessage: z.string().nullable(),
	firstMessageAt: isoDatetimeToDate,
	lastMessageAt: isoDatetimeToDate,
	conversationTitle: z.string().nullable().optional(),
	sharedWithAdmin: z.boolean().default(false),
});

export type BizyUserConversation = z.infer<typeof BizyUserConversation>;

// ─── Detail message ──────────────────────────────────────────────────────────

export const BizyUserMessage = z.object({
	id: z.string(),
	// Nullable because proactive (assistant-first) ChatHistory rows have no
	// user message — Bizy started the conversation. The hydration logic in
	// useBizyGlobalChat skips pushing a user bubble when this is null/empty.
	userMessage: z.string().nullable(),
	bizyResponse: z.string(),
	createdAt: isoDatetimeToDate,
	emotion: z.string().nullable(),
	feedbackType: z.enum(['positive', 'negative']).nullable(),
	feedbackCategory: z.string().nullable(),
	feedbackText: z.string().nullable(),
	pagePath: z.string().nullable(),
	assistantMessage: BizyAssistantMessageNodeListSchema.nullable().optional(),
});

export type BizyUserMessage = z.infer<typeof BizyUserMessage>;

// ─── Detail wrapper ──────────────────────────────────────────────────────────

export const BizyUserConversationDetail = z.object({
	conversationId: z.string(),
	pagePath: z.string().nullable(),
	sharedWithAdmin: z.boolean().default(false),
	messages: z.array(BizyUserMessage),
});

export type BizyUserConversationDetail = z.infer<typeof BizyUserConversationDetail>;

// ─── Fixtures ────────────────────────────────────────────────────────────────

export const bizyUserConversationFixture = (overrides: Partial<BizyUserConversation> = {}): BizyUserConversation => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	messageCount: fixture.int(1, 10),
	firstUserMessage: fixture.description('conversation') as string | null,
	firstMessageAt: fixture.pastDate(7),
	lastMessageAt: fixture.pastDate(1),
	conversationTitle: null,
	sharedWithAdmin: false,
	...overrides,
});

export const bizyUserMessageFixture = (overrides: Partial<BizyUserMessage> = {}): BizyUserMessage => ({
	id: fixture.id(),
	userMessage: fixture.description('question'),
	bizyResponse: fixture.description('answer'),
	createdAt: fixture.pastDate(1),
	emotion: 'curious',
	feedbackType: null,
	feedbackCategory: null,
	feedbackText: null,
	pagePath: '/admin/reports/participation',
	...overrides,
});

export const bizyUserConversationDetailFixture = (overrides: Partial<BizyUserConversationDetail> = {}): BizyUserConversationDetail => ({
	conversationId: fixture.id(),
	pagePath: '/admin/reports/participation',
	sharedWithAdmin: false,
	messages: [bizyUserMessageFixture(), bizyUserMessageFixture()],
	...overrides,
});
